import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Atom, Clock, ExternalLink, FlaskConical } from "lucide-react";

function usePeptideList() {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/vr/catalog").then(({ data }) => {
      const seen = new Set(); const out = [];
      for (const p of data) {
        const key = p.peptide_slug || p.slug;
        if (seen.has(key)) continue; seen.add(key);
        out.push(p);
      }
      setList(out);
    }).catch(() => {});
  }, []);
  return list;
}

function Shell({ title, icon, accent, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[82] flex items-center justify-center p-4" data-testid="vr-research-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl h-[80vh] rounded-3xl bg-[#080c12] border p-0 overflow-hidden flex flex-col" style={{ borderColor: `${accent}44` }}>
        <div className="p-4 border-b border-white/10 flex items-center gap-2" style={{ background: `linear-gradient(90deg, ${accent}18, transparent)` }}>
          <span style={{ color: accent }}>{icon}</span>
          <div className="font-semibold text-white flex-1">{title}</div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function MolecularPanel({ onClose, onAsk }) {
  const list = usePeptideList();
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const pick = (p) => { setSel(p.slug); setData(null); api.get(`/vr/product/${p.slug}`).then(({ data }) => setData(data)).catch(() => {}); };
  useEffect(() => { if (list.length && !sel) pick(list[0]); }, [list]); // eslint-disable-line
  const pep = data?.peptide || {};
  return (
    <Shell title="Molecular Experience" icon={<Atom size={18} />} accent="#8b5cff" onClose={onClose}>
      <div className="flex-1 grid md:grid-cols-[220px_1fr] overflow-hidden">
        <div className="border-r border-white/10 overflow-y-auto">
          {list.map((p) => (
            <button key={p.slug} onClick={() => pick(p)} data-testid={`vr-mol-item-${p.slug}`}
              className={`w-full text-left px-4 py-2.5 text-sm border-b border-white/5 ${sel === p.slug ? "bg-violet-500/15 text-violet-200" : "text-slate-300 hover:bg-white/5"}`}>{p.name}</button>
          ))}
        </div>
        <div className="overflow-y-auto p-6">
          {!data ? <div className="text-slate-500 text-sm font-mono animate-pulse">Loading structure…</div> : (
            <>
              <h2 className="text-3xl font-semibold text-white">{data.name}</h2>
              <div className="text-sm text-violet-300 font-mono mt-1">{pep.scientific_name || (pep.categories || []).join(" · ")}</div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <Kv k="Molecular weight" v={pep.molecular_weight} />
                <Kv k="Molecular formula" v={pep.molecular_formula || pep.formula} />
                <Kv k="Research categories" v={(pep.categories || []).join(", ")} />
                <Kv k="Half-life" v={pep.half_life} />
              </div>
              {pep.sequence && (
                <div className="mt-5">
                  <div className="text-[11px] font-mono text-slate-400 mb-1">SEQUENCE / STRUCTURE</div>
                  <div className="rounded-xl bg-black/40 border border-white/10 p-3 font-mono text-xs text-cyan-200 break-all">{pep.sequence}</div>
                </div>
              )}
              <button onClick={() => { onAsk && onAsk(data.peptide_slug || data.slug); }} className="mt-6 rounded-xl px-4 py-2.5 border border-violet-400/40 text-violet-200 flex items-center gap-2 hover:bg-violet-500/10">
                <FlaskConical size={15} /> Ask a scientist about this
              </button>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}

export function TimelinePanel({ onClose }) {
  const list = usePeptideList();
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const pick = (p) => { setSel(p.slug); setData(null); api.get(`/vr/product/${p.slug}`).then(({ data }) => setData(data)).catch(() => {}); };
  useEffect(() => { if (list.length && !sel) pick(list[0]); }, [list]); // eslint-disable-line
  const refs = (data?.references) || (data?.peptide?.references) || [];
  return (
    <Shell title="Research Timeline" icon={<Clock size={18} />} accent="#38e1ff" onClose={onClose}>
      <div className="flex-1 grid md:grid-cols-[220px_1fr] overflow-hidden">
        <div className="border-r border-white/10 overflow-y-auto">
          {list.map((p) => (
            <button key={p.slug} onClick={() => pick(p)} data-testid={`vr-tl-item-${p.slug}`}
              className={`w-full text-left px-4 py-2.5 text-sm border-b border-white/5 ${sel === p.slug ? "bg-cyan-500/15 text-cyan-200" : "text-slate-300 hover:bg-white/5"}`}>{p.name}</button>
          ))}
        </div>
        <div className="overflow-y-auto p-6">
          {!data ? <div className="text-slate-500 text-sm font-mono animate-pulse">Loading research…</div> : (
            <>
              <h2 className="text-2xl font-semibold text-white mb-1">{data.name} — Research Library</h2>
              <p className="text-sm text-slate-400 mb-5">Peer-reviewed references and study record on file.</p>
              {refs.length === 0 ? <div className="text-slate-500 text-sm">No references catalogued yet for this compound.</div> : (
                <div className="relative pl-5 border-l border-cyan-400/30 space-y-5">
                  {refs.map((r, i) => {
                    const journal = Array.isArray(r) ? r[0] : (r.journal || "Study");
                    const title = Array.isArray(r) ? r[1] : (r.title || r.citation || "Reference");
                    const url = Array.isArray(r) ? r[2] : (r.url || r.pubmed);
                    return (
                      <div key={i} className="relative" data-testid={`vr-tl-ref-${i}`}>
                        <div className="absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full bg-cyan-400" />
                        <div className="text-[11px] font-mono text-cyan-300 uppercase">{journal}</div>
                        <div className="text-sm text-slate-200 mt-0.5">{title}</div>
                        {url && <a href={url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:text-cyan-200 inline-flex items-center gap-1 mt-1"><ExternalLink size={11} /> Open reference</a>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}

const Kv = ({ k, v }) => (<div className="rounded-xl bg-black/30 border border-white/10 p-3"><div className="text-[11px] text-slate-500 font-mono">{k}</div><div className="text-slate-100 text-sm mt-0.5">{v || "—"}</div></div>);
